import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, LogIn, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/Logo';

const formatErr = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const AdminLogin = () => {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (ready && user) navigate('/admin', { replace: true }); }, [ready, user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate('/admin', { replace: true });
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050F2E] p-4" data-testid="admin-login-page">
      <div className="absolute top-8 left-1/2 -translate-x-1/2">
        <Logo to="/" variant="onDark" imgClassName="h-8" />
      </div>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8">
        <div className="h-12 w-12 rounded-xl bg-[#0A2A63] flex items-center justify-center mb-5">
          <Lock className="h-5 w-5 text-white" />
        </div>
        <h1 className="text-2xl font-black text-[#050F2E] tracking-tight">Staff sign in</h1>
        <p className="text-slate-500 text-sm mt-1">Private area — authorized ProofNation staff only.</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-500 font-bold">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-12 rounded-xl" data-testid="admin-login-email" required />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-slate-500 font-bold">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-12 rounded-xl" data-testid="admin-login-password" required />
          </div>
          {error && <p className="text-sm text-red-600 font-semibold" data-testid="admin-login-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-12 font-bold btn-transition" data-testid="admin-login-submit">
            {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in...</> : <><LogIn className="h-4 w-4 mr-2" /> Sign in</>}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
